import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { fadeIn } from '#src/atoms/animations/keyframes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAlertCircle } from '#src/atoms/icons/system/iconAlertCircle.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';

import type { UnsealableOverlay_anyCard } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  position: relative;
`;

const Overlay = styled(Horizontal).attrs({ gap: 0, center: true })`
  opacity: 0;
  display: none;
  :hover > & {
    display: flex;
    position: absolute;
    inset: 0;
    padding: var(--double-unit);
    background-color: var(--c-nl-700);
    border-radius: 12px;
    text-align: center;
    animation: ${fadeIn} 0.45s ease-in-out;
    opacity: 1;
  }
`;

type Props = {
  card: UnsealableOverlay_anyCard;
  isDoublonOfSelectedCardSlug?: boolean;
  children?: ReactNode;
};

export const UnsealableOverlay = ({
  card,
  children,
  isDoublonOfSelectedCardSlug,
}: Props) => {
  const showSealingDoublon = !card.sealed && !card.sealable;
  return (
    <Wrapper>
      {children}
      {!!showSealingDoublon && (
        <Overlay>
          <Vertical>
            <FontAwesomeIcon color="var(--c-white)" icon={iconAlertCircle} />
            <LabelS bold color="var(--c-white)">
              <FormattedMessage defaultMessage="You already sealed the same edition of this card" />
            </LabelS>
          </Vertical>
        </Overlay>
      )}
      {!!isDoublonOfSelectedCardSlug && (
        <Overlay>
          <Vertical>
            <FontAwesomeIcon color="var(--c-white)" icon={iconAlertCircle} />
            <LabelS bold color="var(--c-white)">
              <FormattedMessage defaultMessage="You can only seal one card per edition" />
            </LabelS>
          </Vertical>
        </Overlay>
      )}
    </Wrapper>
  );
};

UnsealableOverlay.fragments = {
  anyCard: gql`
    fragment UnsealableOverlay_anyCard on AnyCardInterface {
      slug
      sealed
      sealable
      unsealableAt
    }
  ` as TypedDocumentNode<UnsealableOverlay_anyCard>,
};
